import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';

import { BehaviorSubject, fromEvent as observableFromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { WindowRefService } from '../../../shared/service/window/window.service';
import { BrowserService } from '../../../shared/service/browser/browser.service';

@Injectable()
export class CarouselService {
    carousel: ElementRef;
    carouselOutterContainer: ElementRef;
    numOfItems: number;
    carouselItems: HTMLElement;
    outterCarouselWidth: number;
    itemWidthWithMargin: number;
    currentPosition = 0;
    leftArrowDisabled$ = new BehaviorSubject<boolean>(false);
    rightArrowDisabled$ = new BehaviorSubject<boolean>(false);
    // Observable to inform if the user is sliding the carousel or clicking on a card
    isSliding$ = new BehaviorSubject<boolean>(false);

    constructor(private browserService: BrowserService,
                private windowRef: WindowRefService,
                @Inject(DOCUMENT) private document: any) {
        if (browserService.isBrowser) {
            observableFromEvent(this.windowRef.nativeWindow, 'resize')
                .pipe(
                    debounceTime(1000),
                    distinctUntilChanged()
                )
                .subscribe(_ => {
                    this.setCarouselInitialConfig(this.carousel, this.carouselOutterContainer);
                });
        }
    }

    setCarouselInitialConfig(c: ElementRef, outterContainer: ElementRef): void {
        this.carousel = c;
        this.carouselOutterContainer = outterContainer;
        this.carouselItems = this.carousel.nativeElement.querySelectorAll('.js-carousel-item');
        this.numOfItems = this.carousel.nativeElement.querySelectorAll('.js-carousel-item').length;
        this.outterCarouselWidth = Math.ceil(parseFloat(getComputedStyle(this.carouselOutterContainer.nativeElement).width));
        const itemStyles = getComputedStyle(this.carouselItems[0]);
        this.itemWidthWithMargin = parseInt(itemStyles.width, 10) +
                                parseInt(itemStyles.marginLeft, 10) +
                                parseInt(itemStyles.marginRight, 10);
        this.carousel.nativeElement.style.width = `${((this.itemWidthWithMargin * this.numOfItems))}px`;
        // this.setArrows();
        this.setCarouselDragEvent(this.carousel);
    }

    setArrows(): void {
        const carouselWidth = parseInt(this.carousel.nativeElement.style.width, 10);
        if (carouselWidth > this.outterCarouselWidth) {
            if (this.currentPosition === 0) {
                this.leftArrowDisabled$.next(true);
                this.rightArrowDisabled$.next(false);
            }
            if (this.currentPosition < 0 && (this.outterCarouselWidth < (carouselWidth + this.currentPosition))) {
                this.leftArrowDisabled$.next(false);
                this.rightArrowDisabled$.next(false);
            }
            if (this.currentPosition < 0 && (this.outterCarouselWidth === (carouselWidth + this.currentPosition))) {
                this.leftArrowDisabled$.next(false);
                this.rightArrowDisabled$.next(true);
            }
        } else {
            this.leftArrowDisabled$.next(true);
            this.rightArrowDisabled$.next(true);
        }
    }

    setCarouselDragEvent(drag?: ElementRef): void {
        const mousedown$ = observableFromEvent(drag.nativeElement, 'mousedown');
        const mouseup$ = observableFromEvent(this.document, 'mouseup');
        const mousemove$ = observableFromEvent(this.document, 'mousemove');
        const touchstart$ = observableFromEvent(drag.nativeElement, 'touchstart');
        const touchend$ = observableFromEvent(this.document, 'touchend');
        const touchmove$ = observableFromEvent(this.document, 'touchmove');

        const mousedrag$ = mousedown$.pipe(switchMap((md: MouseEvent) => {
            // Set the observable to false on mousedown event
            this.isSliding$.next(false);
            let prevX = md.clientX;

            return mousemove$.pipe(
            map((mm: MouseEvent) => {
                mm.preventDefault();
                // Set the observable to true when the user starts sliding the carousel
                this.isSliding$.next(true);

                const newPos = {
                    dx: mm.clientX - prevX
                };
                prevX = mm.clientX;

                return newPos;
            }),
            takeUntil(mouseup$));
        }));

        // Touch event handlers
        const touchdrag$ = touchstart$.pipe(switchMap((ts: TouchEvent) => {
            let prevX = ts.touches[0].clientX;

            return touchmove$.pipe(
            map((tm: TouchEvent) => {
                // tm.preventDefault();
                const newPos = {
                    dx: tm.touches[0].clientX - prevX
                };
                prevX = tm.touches[0].clientX;

                return newPos;
            }),
            takeUntil(touchend$));
        }));

        touchend$.subscribe(evt => {
            this.finishMobileSliding();
        });

        const mouseTouch$ = merge(mousedrag$, touchdrag$);

        mouseTouch$.pipe(filter(newPos => newPos.dx !== 0))
            .subscribe((newPos: { dx: number }) => {

                this.slideCarousel(newPos.dx);
            });
    }

    moveCarousel(direction: string, lArrowDis: boolean, rArrowDis: boolean): void {
        let newPosition: number;
        const carouselPanelWidth = parseInt(this.carousel.nativeElement.style.width, 10);
        if (direction === 'right') {
            if (!rArrowDis) {
                newPosition = this.currentPosition - this.itemWidthWithMargin;
                if (newPosition < (this.outterCarouselWidth - carouselPanelWidth)) {
                    newPosition = +(this.outterCarouselWidth - carouselPanelWidth).toFixed(2);
                }
                this.carousel.nativeElement.style.transform = `translateX(${newPosition}px)`;
                this.currentPosition = newPosition;
            }
            this.setArrows();
        }
        if (direction === 'left') {
            if (!lArrowDis) {
                newPosition = this.currentPosition + this.itemWidthWithMargin;
                if (newPosition < 0) {
                    this.carousel.nativeElement.style.transform = `translateX(${newPosition}px)`;
                    this.currentPosition = newPosition;
                } else {
                    this.carousel.nativeElement.style.transform = `translateX(0px)`;
                    this.currentPosition = 0;
                }
                this.setArrows();
            }
        }
    }

    slideCarousel(newPos): void {
        let newPosition = this.currentPosition + newPos;
        const carouselPanelWidth = parseInt(this.carousel.nativeElement.style.width, 10);

        if (newPosition > 0) newPosition = 0;

        if ((carouselPanelWidth + newPosition) < this.outterCarouselWidth)
            newPosition += (this.outterCarouselWidth - (carouselPanelWidth + newPosition));

        if (newPosition <= 0 && ((carouselPanelWidth + newPosition) >= this.outterCarouselWidth)) {
            this.carousel.nativeElement.style.transform = `translateX(${newPosition}px)`;
            this.currentPosition = newPosition;
        }
        this.setArrows();
    }

    finishMobileSliding(): void {
        const slideToItem = (Math.round(Math.abs(this.currentPosition) / this.itemWidthWithMargin));
        if (slideToItem < (this.numOfItems - 1)) {
            this.carousel.nativeElement.style.transitionDuration = '0.4s';
            this.carousel.nativeElement.style.transform = `translateX(${-(slideToItem * this.itemWidthWithMargin)}px)`;
            this.currentPosition = -(slideToItem * this.itemWidthWithMargin);
        }
    }
}
